import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { serverLink } from "../../../resources/url";
import DataLoader from "../../common/dataLoader/dataLoader";
import ApexChart from "react-apexcharts";
import { useReactToPrint } from "react-to-print";

// ─── Constants ───────────────────────────────────────────────────────────────

const LEVEL_LABELS = {
    '1': '100L (ND I)',   '2': '200L (ND II)',
    '3': '300L (HND I)',  '4': '400L (HND II)',
    'NDI': '100L (ND I)', 'NDII': '200L (ND II)',
    'HNDI': '300L (HND I)', 'HNDII': '400L (HND II)',
};
const LEVEL_ORDER = ['100L (ND I)', '200L (ND II)', '300L (HND I)', '400L (HND II)'];

const TABS = [
    { key: 'session',   label: 'Session'      },
    { key: 'year',      label: 'Year'          },
    { key: 'mode',      label: 'Mode of Entry' },
    { key: 'level',     label: 'Level'         },
    { key: 'dept',      label: 'Department'    },
    { key: 'status',    label: 'Status'        },
    { key: 'programme', label: 'Programme'     },
    { key: 'gender',    label: 'Gender'        },
];

const TAB_FULL_LABEL = {
    session: 'By Admission Session', year: 'By Admission Year',
    mode: 'By Mode of Entry', level: 'By Student Level',
    dept: 'By Department', status: 'By Admission Status',
    programme: 'By Programme', gender: 'By Gender',
};

function TabIcon({ name, color = 'currentColor' }) {
    const s = { width: 15, height: 15, viewBox: '0 0 24 24', strokeWidth: 2,
        stroke: color, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };
    const icons = {
        session: <><rect x="4" y="5" width="16" height="16" rx="2"/><path d="M16 3v4M8 3v4M4 11h16"/><path d="M8 15h2v2H8z"/></>,
        year:    <><path d="M3 12l9-9 9 9"/><path d="M9 21V9h6v12"/></>,
        mode:    <><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M13 6h3a2 2 0 0 1 2 2v7"/><path d="M11 18H8a2 2 0 0 1-2-2V9"/></>,
        level:   <><path d="M5 9l4-4 4 4 4-4"/><path d="M5 19l4-4 4 4 4-4"/></>,
        dept:    <><path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1"/><path d="M3 21V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v16"/></>,
        status:  <><path d="M3 12h3l3-9 4 18 3-9h3"/></>,
        programme:<><path d="M3 19a9 9 0 0 1 9 0 9 9 0 0 1 9 0M3 6a9 9 0 0 1 9 0 9 9 0 0 1 9 0M3 6v13M12 6v13M21 6v13"/></>,
        gender:  <><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.87"/></>,
    };
    return <svg xmlns="http://www.w3.org/2000/svg" {...s}>{icons[name]}</svg>;
}

const C = {
    total:       '#6c757d',
    admitted:    '#2fb344',
    enrolled:    '#206bc4',
    notEnrolled: '#f76707',
    rate:        '#ae3ec9',
    palette: ['#206bc4','#2fb344','#f76707','#6c757d','#ae3ec9','#d63939','#4299e1','#f59f00'],
};

const BASE = { toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: true, speed: 600 } };

// ─── Module-level utility ─────────────────────────────────────────────────────

const pctNum = (n, d) => d === 0 ? 0 : parseFloat(((n / d) * 100).toFixed(1));

// ─── Shared chart sub-components ─────────────────────────────────────────────

function AreaChart({ breakdown, height = 280 }) {
    const cats   = breakdown.map(([k]) => k);
    const series = [
        { name: 'Total',    data: breakdown.map(([, v]) => v.total)    },
        { name: 'Admitted', data: breakdown.map(([, v]) => v.admitted) },
        { name: 'Enrolled', data: breakdown.map(([, v]) => v.enrolled) },
    ];
    const options = {
        chart: { ...BASE, type: 'area' },
        stroke: { curve: 'smooth', width: 2 },
        fill:   { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.03 } },
        dataLabels: { enabled: false },
        xaxis: { categories: cats, labels: { style: { fontSize: '11px' } } },
        yaxis: { labels: { formatter: v => v.toLocaleString() } },
        colors: [C.total, C.admitted, C.enrolled],
        legend: { position: 'top', fontSize: '12px' },
        tooltip: { shared: true, intersect: false },
        grid:   { borderColor: '#f0f0f0', strokeDashArray: 3 },
    };
    return <ApexChart options={options} series={series} type="area" height={height} />;
}

function ColumnBarChart({ breakdown, height = 290 }) {
    const cats   = breakdown.map(([k]) => k);
    const series = [
        { name: 'Total',        data: breakdown.map(([, v]) => v.total)       },
        { name: 'Admitted',     data: breakdown.map(([, v]) => v.admitted)    },
        { name: 'Enrolled',     data: breakdown.map(([, v]) => v.enrolled)    },
        { name: 'Not Enrolled', data: breakdown.map(([, v]) => v.notEnrolled) },
    ];
    const options = {
        chart: { ...BASE, type: 'bar' },
        plotOptions: { bar: { borderRadius: 3, columnWidth: '58%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: cats, labels: { rotate: -30, trim: true, style: { fontSize: '10px' } } },
        yaxis: { labels: { formatter: v => v.toLocaleString() } },
        colors: [C.total, C.admitted, C.enrolled, C.notEnrolled],
        legend: { position: 'top', fontSize: '12px' },
        tooltip: { shared: true, intersect: false },
        grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
    };
    return <ApexChart options={options} series={series} type="bar" height={height} />;
}

function HBarChart({ breakdown, topN = 12 }) {
    const top    = breakdown.slice(0, topN);
    const cats   = top.map(([k]) => k);
    const series = [
        { name: 'Admitted',     data: top.map(([, v]) => v.admitted)    },
        { name: 'Enrolled',     data: top.map(([, v]) => v.enrolled)    },
        { name: 'Not Enrolled', data: top.map(([, v]) => v.notEnrolled) },
    ];
    const options = {
        chart: { ...BASE, type: 'bar' },
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '62%',
            dataLabels: { position: 'top' } } },
        dataLabels: { enabled: true, offsetX: 6, style: { fontSize: '10px', colors: ['#354052'] } },
        xaxis: { categories: cats, labels: { formatter: v => v.toLocaleString(), style: { fontSize: '10px' } } },
        yaxis: { labels: { style: { fontSize: '10px' }, maxWidth: 190 } },
        colors: [C.admitted, C.enrolled, C.notEnrolled],
        legend: { position: 'top', fontSize: '12px' },
        tooltip: { shared: true, intersect: false },
        grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
    };
    const h = Math.max(320, top.length * 46 + 80);
    return <ApexChart options={options} series={series} type="bar" height={h} />;
}

function DonutChart({ breakdown, height = 300 }) {
    const labels = breakdown.map(([k]) => k);
    const values = breakdown.map(([, v]) => v.total);
    const grand  = values.reduce((a, b) => a + b, 0);
    const options = {
        chart: { ...BASE, type: 'donut' },
        labels,
        colors: C.palette,
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: { formatter: (val) => `${val.toFixed(1)}%` },
        plotOptions: { pie: { donut: { size: '64%', labels: { show: true,
            total: { show: true, label: 'Total', fontSize: '13px', fontWeight: 600,
                formatter: () => grand.toLocaleString() },
            value: { fontSize: '22px', fontWeight: 700, formatter: v => Number(v).toLocaleString() },
        }}}},
        tooltip: { y: { formatter: v => v.toLocaleString() } },
    };
    return <ApexChart options={options} series={values} type="donut" height={height} />;
}

function RadialBarChart({ breakdown }) {
    const sorted = [...breakdown].sort((a, b) => {
        const ai = LEVEL_ORDER.indexOf(a[0]);
        const bi = LEVEL_ORDER.indexOf(b[0]);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    const labels = sorted.map(([k]) => k);
    const rates  = sorted.map(([, v]) => pctNum(v.enrolled, v.admitted));
    const options = {
        chart: { ...BASE, type: 'radialBar' },
        plotOptions: {
            radialBar: {
                offsetY: -10, startAngle: -130, endAngle: 130,
                hollow: { size: '40%', background: 'transparent' },
                track: { background: '#f0f0f0', strokeWidth: '100%' },
                dataLabels: {
                    name:  { fontSize: '12px', offsetY: -8 },
                    value: { fontSize: '16px', fontWeight: 700, formatter: v => `${v}%` },
                    total: { show: true, label: 'Avg Rate',
                        formatter: (w) => {
                            const arr = w.globals.seriesTotals.filter(n => n > 0);
                            if (!arr.length) return '0%';
                            return `${(arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(1)}%`;
                        },
                    },
                },
            },
        },
        colors: ['#206bc4', '#2fb344', '#f76707', '#ae3ec9'],
        labels,
        legend: { show: true, position: 'bottom', fontSize: '11px' },
    };
    return <ApexChart options={options} series={rates} type="radialBar" height={340} />;
}

// ─── Main component ───────────────────────────────────────────────────────────

function StudentEnrollmentAnalytics(props) {
    const [isLoading, setIsLoading]     = useState(true);
    const [students, setStudents]       = useState([]);
    const [departments, setDepartments] = useState([]);
    const [activeTab, setActiveTab]     = useState('session');

    useEffect(() => { getData(); }, []);

    const getData = async () => {
        try {
            const result = await axios.get(`${serverLink}staff/report/enrollment/analytics`);
            setStudents(result.data.Students || []);
            setDepartments(result.data.Departments || []);
        } catch (e) {
            console.log("NETWORK ERROR");
        } finally {
            setIsLoading(false);
        }
    };

    // ─── predicates ─────────────────────────────────────────────────────────────

    const isAdmitted    = (s) => s.APP_STATUS === 'Active' && String(s.ACCEPT_STATUS) === '1';
    const isEnrolled    = (s) => isAdmitted(s) && s.STUDENTID !== '' && String(s.IS_STUDENT) === '1';
    const isNotEnrolled = (s) => isAdmitted(s) && s.STUDENTID === '' && String(s.IS_STUDENT) === '0';
    const formatLevel   = (val) => LEVEL_LABELS[val] || val || 'N/A';
    const pct = (n, d) => d === 0 ? '0%' : `${((n / d) * 100).toFixed(1)}%`;

    // ─── totals ──────────────────────────────────────────────────────────────────

    const totalStudents    = students.length;
    const totalAdmitted    = students.filter(isAdmitted).length;
    const totalEnrolled    = students.filter(isEnrolled).length;
    const totalNotEnrolled = students.filter(isNotEnrolled).length;
    const totalPending     = students.filter(s => s.APP_STATUS === 'Pending').length;

    // ─── print / export ──────────────────────────────────────────────────────────

    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Enrollment-Analytics-${new Date().toISOString().split('T')[0]}`,
        pageStyle: `
            @page { size: A4 portrait; margin: 10mm; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #1a1a2e; }
            h1 { font-size: 20px; margin: 0 0 4px; }
            h2 { font-size: 14px; margin: 18px 0 7px; color: #206bc4;
                 border-bottom: 2px solid #206bc4; padding-bottom: 4px; }
            p  { font-size: 11px; margin: 0 0 10px; color: #555; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
            thead th { background:#206bc4 !important; color:#fff !important; padding:5px 7px; text-align:left; font-size:11px; white-space:nowrap; }
            tbody td { padding:4px 7px; border-bottom:1px solid #e9ecef; font-size:11px; }
            tbody tr:nth-child(even) td { background:#f0f4ff !important; }
            tfoot td { padding:4px 7px; font-weight:700; background:#dce7ff !important; font-size:11px; }
            .kpi-row { display:flex; gap:10px; margin-bottom:14px; }
            .kpi-box { flex:1; border:1px solid #206bc4; border-radius:6px; padding:10px; text-align:center; background:#f0f4ff !important; }
            .kpi-val { font-size:22px; font-weight:700; color:#206bc4 !important; }
            .kpi-lbl { font-size:10px; text-transform:uppercase; color:#555; letter-spacing:.05em; font-weight:600; }
            .kpi-sub { font-size:10px; color:#444; margin-top:2px; }
            .two-col  { display:grid; grid-template-columns:1fr; gap:14px; }
            .three-col{ display:grid; grid-template-columns:1fr; gap:14px; }
            .chart-card { border:1px solid #dce7ff; border-radius:6px; padding:10px; margin-bottom:14px; background:#f8faff !important; }
            .chart-card-title { font-size:13px; font-weight:700; color:#206bc4; margin-bottom:6px; }
            .pg-break { page-break-before: always; }
            .report-header-bar { background:#206bc4 !important; color:#fff !important; padding:12px 16px; border-radius:6px; margin-bottom:14px; }
        `,
    });

    const exportExcel = () => {
        const date      = new Date().toLocaleDateString('en-GB');
        const thStyle   = 'background:#206bc4;color:#fff;padding:5px 8px;font-weight:bold;border:1px solid #1a56a0;';
        const tdStyle   = 'padding:4px 8px;border:1px solid #dee2e6;';
        const tdAlt     = 'padding:4px 8px;border:1px solid #dee2e6;background:#f8f9fc;';
        const hdStyle   = 'font-size:13px;font-weight:bold;color:#206bc4;padding:8px 4px;';

        const makeTbl = (title, headers, rows) => {
            const head = `<tr>${headers.map(h => `<th style="${thStyle}">${h}</th>`).join('')}</tr>`;
            const body = rows.map((r, i) =>
                `<tr>${r.map(c => `<td style="${i % 2 === 0 ? tdStyle : tdAlt}">${c}</td>`).join('')}</tr>`
            ).join('');
            const foot = `<tr>${headers.map((_, i) => {
                if (i === 0) return `<td style="font-weight:bold;${tdStyle}">Total</td>`;
                const sum = rows.reduce((s, r) => s + (typeof r[i] === 'number' ? r[i] : 0), 0);
                return `<td style="font-weight:bold;${tdStyle}">${sum > 0 ? sum.toLocaleString() : ''}</td>`;
            }).join('')}</tr>`;
            return `
                <tr><td colspan="${headers.length}" style="${hdStyle}">${title}</td></tr>
                <table border="0" style="width:100%;border-collapse:collapse;">
                    <thead>${head}</thead><tbody>${body}</tbody><tfoot>${foot}</tfoot>
                </table>
                <tr><td>&nbsp;</td></tr>`;
        };

        const stdH  = ['Group','Total','Admitted','Enrolled','Not Enrolled','Enrolment Rate'];
        const stdR  = (bd) => bd.map(([k, v]) => [k || 'N/A', v.total, v.admitted, v.enrolled, v.notEnrolled, pct(v.enrolled, v.admitted)]);
        const deptMap = departments.reduce((acc, d) => { acc[d.DEPT_CODE] = d.DEPARTMENT; return acc; }, {});

        const sessionBD = computeBreakdown(s => s.ADMISSION_SESSION || 'N/A', (a, b) => a[0].localeCompare(b[0]));
        const yearBD    = computeBreakdown(s => s.ADMISSION_YEAR ? String(s.ADMISSION_YEAR) : 'N/A', (a, b) => Number(a[0]) - Number(b[0]));
        const modeBD    = computeBreakdown(s => s.MODE_OF_ENTRY || 'Application');
        const levelBD   = computeBreakdown(s => formatLevel(s.STUDENT_LEVEL));
        const deptBD    = computeBreakdown(s => s.DEPT_CODE || s.DEPART_ID || 'N/A');
        const statusBD  = computeBreakdown(s => s.APP_STATUS || 'N/A');
        const progBD    = computeBreakdown(s => s.PROGRAMME || 'N/A');
        const genderBD  = computeBreakdown(s => s.GENDER || 'N/A');

        const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="utf-8"/></head>
        <body>
        <table style="font-family:Arial;font-size:10px;width:100%;">
            <tr><td colspan="7" style="font-size:16px;font-weight:bold;padding:6px 4px;">ODFeL — Student Enrollment Analytics Report</td></tr>
            <tr><td colspan="7" style="color:#555;padding:2px 4px;">Generated: ${date} &nbsp;|&nbsp; Total Records: ${totalStudents.toLocaleString()}</td></tr>
            <tr><td>&nbsp;</td></tr>
            <tr><td colspan="7" style="${hdStyle}">SUMMARY STATISTICS</td></tr>
            <table border="0" style="width:60%;border-collapse:collapse;">
                <thead><tr>
                    <th style="${thStyle}">Metric</th><th style="${thStyle}">Count</th><th style="${thStyle}">Percentage</th>
                </tr></thead>
                <tbody>
                    <tr><td style="${tdStyle}">Total Students</td><td style="${tdStyle}">${totalStudents.toLocaleString()}</td><td style="${tdStyle}">100%</td></tr>
                    <tr><td style="${tdAlt}">Admitted</td><td style="${tdAlt}">${totalAdmitted.toLocaleString()}</td><td style="${tdAlt}">${pct(totalAdmitted,totalStudents)} of total</td></tr>
                    <tr><td style="${tdStyle}">Enrolled</td><td style="${tdStyle}">${totalEnrolled.toLocaleString()}</td><td style="${tdStyle}">${pct(totalEnrolled,totalAdmitted)} of admitted</td></tr>
                    <tr><td style="${tdAlt}">Not Enrolled</td><td style="${tdAlt}">${totalNotEnrolled.toLocaleString()}</td><td style="${tdAlt}">${pct(totalNotEnrolled,totalAdmitted)} of admitted</td></tr>
                    <tr><td style="${tdStyle}">Pending</td><td style="${tdStyle}">${totalPending.toLocaleString()}</td><td style="${tdStyle}">${pct(totalPending,totalStudents)} of total</td></tr>
                </tbody>
            </table>
            <tr><td>&nbsp;</td></tr>
            ${makeTbl('BREAKDOWN BY ADMISSION SESSION', stdH, stdR(sessionBD))}
            ${makeTbl('BREAKDOWN BY ADMISSION YEAR',    stdH, stdR(yearBD))}
            ${makeTbl('BREAKDOWN BY MODE OF ENTRY',     stdH, stdR(modeBD))}
            ${makeTbl('BREAKDOWN BY STUDENT LEVEL',     stdH, stdR(levelBD))}
            ${makeTbl('BREAKDOWN BY DEPARTMENT',        ['Code','Department','Total','Admitted','Enrolled','Not Enrolled','Enrolment Rate'],
                deptBD.map(([k, v]) => [k, deptMap[k]||k, v.total, v.admitted, v.enrolled, v.notEnrolled, pct(v.enrolled,v.admitted)]))}
            ${makeTbl('BREAKDOWN BY ADMISSION STATUS',  ['Status','Count','% of Total'],
                statusBD.map(([k,v]) => [{Active:'Admitted (Active)',Pending:'Not Admitted (Pending)'}[k]||k, v.total, pct(v.total,totalStudents)]))}
            ${makeTbl('BREAKDOWN BY PROGRAMME',         stdH, stdR(progBD))}
            ${makeTbl('BREAKDOWN BY GENDER',            stdH, stdR(genderBD))}
        </table>
        </body></html>`;

        const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = `Enrollment-Analytics-${new Date().toISOString().split('T')[0]}.xls`;
        a.click(); URL.revokeObjectURL(url);
    };

    // ─── breakdown helper ────────────────────────────────────────────────────────

    const computeBreakdown = (groupFn, sortFn) => {
        const groups = students.reduce((acc, s) => {
            const key = groupFn(s);
            if (!acc[key]) acc[key] = { total: 0, admitted: 0, enrolled: 0, notEnrolled: 0 };
            acc[key].total++;
            if (isAdmitted(s))    acc[key].admitted++;
            if (isEnrolled(s))    acc[key].enrolled++;
            if (isNotEnrolled(s)) acc[key].notEnrolled++;
            return acc;
        }, {});
        const entries = Object.entries(groups);
        return sortFn ? entries.sort(sortFn) : entries.sort((a, b) => b[1].total - a[1].total);
    };

    // ─── table ───────────────────────────────────────────────────────────────────

    const BreakdownTable = ({ headers, rows }) => (
        <div className="table-responsive">
            <table className="table table-vcenter table-striped table-sm mb-0">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 40 }}>#</th>
                        {headers.map((h, i) => <th key={i}>{h}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0
                        ? <tr><td colSpan={headers.length + 1} className="text-center text-muted py-4">No data available</td></tr>
                        : rows.map((row, i) => (
                            <tr key={i}>
                                <td className="text-muted">{i + 1}</td>
                                {row.map((cell, j) => <td key={j}>{cell}</td>)}
                            </tr>
                        ))}
                </tbody>
                <tfoot className="table-light fw-bold">
                    <tr>
                        <td colSpan={2}>Total</td>
                        {rows.length > 0 && Array.from({ length: rows[0].length - 1 }, (_, i) => (
                            <td key={i}>
                                {typeof rows[0][i + 1] === 'number'
                                    ? rows.reduce((s, r) => s + (typeof r[i + 1] === 'number' ? r[i + 1] : 0), 0).toLocaleString()
                                    : ''}
                            </td>
                        ))}
                    </tr>
                </tfoot>
            </table>
        </div>
    );

    const standardBreakdown = (breakdown) => {
        const headers = ['Group', 'Total', 'Admitted', 'Enrolled', 'Not Enrolled', 'Enrolment Rate'];
        const rows = breakdown.map(([key, v]) => [
            key || 'N/A', v.total, v.admitted, v.enrolled, v.notEnrolled,
            pct(v.enrolled, v.admitted),
        ]);
        return <BreakdownTable headers={headers} rows={rows} />;
    };

    // ─── tab content ─────────────────────────────────────────────────────────────

    const renderTabContent = () => {
        switch (activeTab) {
            case 'session':
                return standardBreakdown(computeBreakdown(s => s.ADMISSION_SESSION || 'N/A', (a, b) => a[0].localeCompare(b[0])));
            case 'year':
                return standardBreakdown(computeBreakdown(s => s.ADMISSION_YEAR ? String(s.ADMISSION_YEAR) : 'N/A', (a, b) => Number(a[0]) - Number(b[0])));
            case 'mode':
                return standardBreakdown(computeBreakdown(s => s.MODE_OF_ENTRY || 'Application'));
            case 'level': {
                const data = computeBreakdown(s => formatLevel(s.STUDENT_LEVEL));
                return standardBreakdown([...data].sort((a, b) => {
                    const ai = LEVEL_ORDER.indexOf(a[0]); const bi = LEVEL_ORDER.indexOf(b[0]);
                    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                }));
            }
            case 'dept': {
                const deptMap = departments.reduce((acc, d) => { acc[d.DEPT_CODE] = d.DEPARTMENT; return acc; }, {});
                const data    = computeBreakdown(s => s.DEPT_CODE || s.DEPART_ID || 'N/A');
                return <BreakdownTable
                    headers={['Code','Department','Total','Admitted','Enrolled','Not Enrolled','Enrolment Rate']}
                    rows={data.map(([k, v]) => [k, deptMap[k]||k, v.total, v.admitted, v.enrolled, v.notEnrolled, pct(v.enrolled,v.admitted)])} />;
            }
            case 'status': {
                const data = computeBreakdown(s => s.APP_STATUS || 'N/A');
                return <BreakdownTable
                    headers={['Status','Count','% of Total']}
                    rows={data.map(([k,v]) => [{Active:'Admitted (Active)',Pending:'Not Admitted (Pending)'}[k]||k, v.total, pct(v.total,totalStudents)])} />;
            }
            case 'programme':
                return standardBreakdown(computeBreakdown(s => s.PROGRAMME || 'N/A'));
            case 'gender':
                return standardBreakdown(computeBreakdown(s => s.GENDER || 'N/A'));
            default: return null;
        }
    };

    // ─── chart section ───────────────────────────────────────────────────────────

    const renderCharts = () => {
        const yearBD    = computeBreakdown(s => s.ADMISSION_YEAR ? String(s.ADMISSION_YEAR) : 'N/A', (a, b) => Number(a[0]) - Number(b[0]));
        const sessionBD = computeBreakdown(s => s.ADMISSION_SESSION || 'N/A', (a, b) => a[0].localeCompare(b[0]));
        const genderBD  = computeBreakdown(s => s.GENDER || 'N/A');
        const modeBD    = computeBreakdown(s => s.MODE_OF_ENTRY || 'Application');
        const statusBD  = computeBreakdown(s => s.APP_STATUS || 'N/A').map(([k, v]) => [
            { Active: 'Admitted', Pending: 'Pending' }[k] || k, v,
        ]);
        const levelBD   = computeBreakdown(s => formatLevel(s.STUDENT_LEVEL));
        const deptBD    = computeBreakdown(s => s.DEPT_CODE || s.DEPART_ID || 'N/A');
        const progBD    = computeBreakdown(s => s.PROGRAMME || 'N/A');

        const CC = ({ title, subtitle, col, children }) => (
            <div className={col}>
                <div className="card h-100">
                    <div className="card-header">
                        <div>
                            <h3 className="card-title mb-0">{title}</h3>
                            {subtitle && <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>{subtitle}</p>}
                        </div>
                    </div>
                    <div className="card-body">{children}</div>
                </div>
            </div>
        );

        return (
            <>
                <div className="row row-deck row-cards">
                    <CC col="col-lg-8" title="Year-over-Year Enrolment Trend" subtitle="Total, admitted and enrolled students per admission year">
                        <AreaChart breakdown={yearBD} height={280} />
                    </CC>
                    <CC col="col-lg-4" title="Gender Distribution" subtitle="Share of male vs female students">
                        <DonutChart breakdown={genderBD} height={280} />
                    </CC>
                </div>
                <div className="row row-deck row-cards mt-0">
                    <CC col="col-12" title="Enrolment by Admission Session" subtitle="Headcount breakdown across all sessions">
                        <ColumnBarChart breakdown={sessionBD} height={290} />
                    </CC>
                </div>
                <div className="row row-deck row-cards mt-0">
                    <CC col="col-lg-7" title="Enrolment by Department" subtitle="Top 12 departments — admitted vs enrolled vs not enrolled">
                        <HBarChart breakdown={deptBD} topN={12} />
                    </CC>
                    <CC col="col-lg-5" title="Enrolment Rate by Student Level" subtitle="% of admitted students who enrolled per level">
                        <RadialBarChart breakdown={levelBD} />
                    </CC>
                </div>
                <div className="row row-deck row-cards mt-0">
                    <CC col="col-lg-4" title="Mode of Entry" subtitle="Application vs Transfer">
                        <DonutChart breakdown={modeBD} height={280} />
                    </CC>
                    <CC col="col-lg-4" title="Admission Status" subtitle="Admitted (Active) vs Pending applicants">
                        <DonutChart breakdown={statusBD} height={280} />
                    </CC>
                    <CC col="col-lg-4" title="Enrolled vs Not Enrolled" subtitle="Among admitted students">
                        <DonutChart breakdown={[['Enrolled',{total:totalEnrolled}],['Not Enrolled',{total:totalNotEnrolled}]]} height={280} />
                    </CC>
                </div>
                <div className="row row-deck row-cards mt-0">
                    <CC col="col-12" title="Enrolment by Programme" subtitle="Top 12 programmes — admitted vs enrolled vs not enrolled">
                        <HBarChart breakdown={progBD} topN={12} />
                    </CC>
                </div>
            </>
        );
    };

    // ─── render ──────────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="container-xl" style={{ height: '700px' }}>
                <div className="page-header d-print-none"><DataLoader /></div>
            </div>
        );
    }

    const tabCounts = {
        session:   new Set(students.map(s => s.ADMISSION_SESSION || 'N/A')).size,
        year:      new Set(students.map(s => s.ADMISSION_YEAR ? String(s.ADMISSION_YEAR) : 'N/A')).size,
        mode:      new Set(students.map(s => s.MODE_OF_ENTRY || 'Application')).size,
        level:     new Set(students.map(s => formatLevel(s.STUDENT_LEVEL))).size,
        dept:      new Set(students.map(s => s.DEPT_CODE || s.DEPART_ID || 'N/A')).size,
        status:    new Set(students.map(s => s.APP_STATUS || 'N/A')).size,
        programme: new Set(students.map(s => s.PROGRAMME || 'N/A')).size,
        gender:    new Set(students.map(s => s.GENDER || 'N/A')).size,
    };

    const allBreakdowns = {
        year:      computeBreakdown(s => s.ADMISSION_YEAR ? String(s.ADMISSION_YEAR) : 'N/A', (a, b) => Number(a[0]) - Number(b[0])),
        session:   computeBreakdown(s => s.ADMISSION_SESSION || 'N/A', (a, b) => a[0].localeCompare(b[0])),
        gender:    computeBreakdown(s => s.GENDER || 'N/A'),
        mode:      computeBreakdown(s => s.MODE_OF_ENTRY || 'Application'),
        status:    computeBreakdown(s => s.APP_STATUS || 'N/A').map(([k, v]) => [{ Active:'Admitted', Pending:'Pending' }[k]||k, v]),
        level:     computeBreakdown(s => formatLevel(s.STUDENT_LEVEL)),
        dept:      computeBreakdown(s => s.DEPT_CODE || s.DEPART_ID || 'N/A'),
        programme: computeBreakdown(s => s.PROGRAMME || 'N/A'),
    };

    return (
        <>
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Registration / Admission</div>
                            <h2 className="page-title">Student Enrollment Analytics</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="18" height="18"
                                        viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2"/>
                                        <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4"/>
                                        <rect x="7" y="13" width="10" height="8" rx="2"/>
                                    </svg>
                                    Print Report
                                </button>
                                <button className="btn btn-success" onClick={exportExcel}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="18" height="18"
                                        viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
                                        <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"/>
                                        <path d="M8 11h8"/><path d="M8 15h5"/><path d="M11 19v-8"/>
                                    </svg>
                                    Export Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">

                    {/* ── KPI cards ─────────────────────────────────────────────────── */}
                    <div className="row row-deck row-cards mb-3">
                        {[
                            { label: 'Total Students',  value: totalStudents,    sub: null,                                               cls: ''             },
                            { label: 'Admitted',        value: totalAdmitted,    sub: `${pct(totalAdmitted,totalStudents)} of total`,     cls: 'text-success' },
                            { label: 'Enrolled',        value: totalEnrolled,    sub: `${pct(totalEnrolled,totalAdmitted)} of admitted`,  cls: 'text-primary' },
                            { label: 'Not Enrolled',    value: totalNotEnrolled, sub: `${pct(totalNotEnrolled,totalAdmitted)} of admitted`, cls: 'text-warning' },
                            { label: 'Pending',         value: totalPending,     sub: `${pct(totalPending,totalStudents)} of total`,      cls: 'text-secondary' },
                        ].map(({ label, value, sub, cls }) => (
                            <div key={label} className="col-6 col-lg">
                                <div className="card">
                                    <div className="card-body p-3 text-center">
                                        <div className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
                                        <div className={`display-6 fw-bold ${cls}`}>{value.toLocaleString()}</div>
                                        {sub && <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>{sub}</div>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Banner ────────────────────────────────────────────────────── */}
                    <div className="alert alert-info mb-3" role="alert">
                        <strong>Overall Enrolment Rate: {pct(totalEnrolled, totalAdmitted)}</strong>
                        {' — '}<strong>{totalEnrolled.toLocaleString()}</strong> enrolled out of{' '}
                        <strong>{totalAdmitted.toLocaleString()}</strong> admitted.{' '}
                        <strong>{totalNotEnrolled.toLocaleString()}</strong> admitted students are yet to enrol.
                    </div>

                    {/* ── Table tabs ────────────────────────────────────────────────── */}
                    <div style={{ background: '#f0f4fd', borderRadius: '14px', padding: '7px', marginBottom: '16px', overflowX: 'auto' }}>
                        <div style={{ display: 'flex', gap: '4px', minWidth: 'max-content' }}>
                            {TABS.map(tab => {
                                const active = activeTab === tab.key;
                                return (
                                    <button
                                        key={tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '7px',
                                            padding: '9px 15px', border: 'none', borderRadius: '10px',
                                            cursor: 'pointer', fontSize: '13px',
                                            fontWeight: active ? 600 : 400,
                                            background: active ? '#206bc4' : 'transparent',
                                            color: active ? '#fff' : '#64748b',
                                            transition: 'background 0.15s, color 0.15s',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <TabIcon name={tab.key} color={active ? '#fff' : '#94a3b8'} />
                                        {tab.label}
                                        <span style={{
                                            fontSize: '10px', fontWeight: 700,
                                            padding: '1px 7px', borderRadius: '20px',
                                            background: active ? 'rgba(255,255,255,0.22)' : '#dde3f0',
                                            color: active ? '#fff' : '#64748b',
                                            minWidth: '20px', textAlign: 'center',
                                        }}>
                                            {tabCounts[tab.key]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-header" style={{ borderBottom: '2px solid #e9ecef', paddingBottom: '14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ width: '4px', height: '22px', background: '#206bc4', borderRadius: '3px', flexShrink: 0 }} />
                                <div>
                                    <h3 className="card-title mb-0">{TAB_FULL_LABEL[activeTab]}</h3>
                                    <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.76rem' }}>
                                        {tabCounts[activeTab]} group{tabCounts[activeTab] !== 1 ? 's' : ''} &nbsp;·&nbsp; Enrollment Breakdown
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            {renderTabContent()}
                        </div>
                    </div>

                    {/* ── Chart trends ──────────────────────────────────────────────── */}
                    <div className="mb-2">
                        <h3 className="mb-1" style={{ fontWeight: 600, color: '#354052' }}>Chart Trends</h3>
                        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Visual breakdown of enrolment data across all dimensions</p>
                    </div>
                    {renderCharts()}

                </div>
            </div>

            {/* ── Hidden printable report (wide enough for charts to render) ── */}
            <div style={{ position: 'absolute', top: '-9999px', left: 0, width: '1100px', visibility: 'hidden' }}>
                <PrintableReport
                    ref={printRef}
                    totals={{ totalStudents, totalAdmitted, totalEnrolled, totalNotEnrolled, totalPending }}
                    breakdowns={allBreakdowns}
                    departments={departments}
                    pct={pct}
                />
            </div>
        </>
    );
}

// ─── Printable report ─────────────────────────────────────────────────────────

const PrintableReport = React.forwardRef(({ totals, breakdowns, departments, pct }, ref) => {
    const { totalStudents, totalAdmitted, totalEnrolled, totalNotEnrolled, totalPending } = totals;
    const deptMap = departments.reduce((acc, d) => { acc[d.DEPT_CODE] = d.DEPARTMENT; return acc; }, {});
    const date    = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

    const SectionTable = ({ title, headers, rows }) => (
        <div style={{ marginBottom: 16 }}>
            <h2>{title}</h2>
            <table>
                <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                <tbody>
                    {rows.map((row, i) => <tr key={i}>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
                </tbody>
                <tfoot>
                    <tr>
                        {headers.map((_, i) => {
                            if (i === 0) return <td key={i}>Total</td>;
                            const sum = rows.reduce((s, r) => s + (typeof r[i] === 'number' ? r[i] : 0), 0);
                            return <td key={i}>{sum > 0 ? sum.toLocaleString() : ''}</td>;
                        })}
                    </tr>
                </tfoot>
            </table>
        </div>
    );

    const ChartCard = ({ title, children, style }) => (
        <div className="chart-card" style={style}>
            <div className="chart-card-title">{title}</div>
            {children}
        </div>
    );

    const stdH = ['Group','Total','Admitted','Enrolled','Not Enrolled','Enrolment Rate'];
    const stdR = (bd) => bd.map(([k, v]) => [k||'N/A', v.total, v.admitted, v.enrolled, v.notEnrolled, pct(v.enrolled, v.admitted)]);

    return (
        <div ref={ref} style={{ padding: '6mm', fontFamily: 'Arial, sans-serif', width: '100%' }}>

            {/* Report header */}
            <div className="report-header-bar" style={{ textAlign: 'center' }}>
                <h1 style={{ margin: 0, color: '#fff' }}>ODFeL — Kaduna Polytechnic</h1>
                <p style={{ margin: '4px 0 0', color: '#e0ecff', fontSize: '12px' }}>
                    Student Enrollment Analytics Report &nbsp;|&nbsp; Generated: {date}
                </p>
            </div>

            {/* KPI row */}
            <div className="kpi-row">
                {[
                    ['Total Students',    totalStudents,    null],
                    ['Admitted',          totalAdmitted,    `${pct(totalAdmitted,totalStudents)} of total`],
                    ['Enrolled',          totalEnrolled,    `${pct(totalEnrolled,totalAdmitted)} of admitted`],
                    ['Not Enrolled',      totalNotEnrolled, `${pct(totalNotEnrolled,totalAdmitted)} of admitted`],
                    ['Pending',           totalPending,     `${pct(totalPending,totalStudents)} of total`],
                ].map(([lbl, val, sub]) => (
                    <div key={lbl} className="kpi-box">
                        <div className="kpi-lbl">{lbl}</div>
                        <div className="kpi-val">{val.toLocaleString()}</div>
                        {sub && <div className="kpi-sub">{sub}</div>}
                    </div>
                ))}
            </div>

            {/* ── Tables ── */}
            <SectionTable title="By Admission Session"   headers={stdH} rows={stdR(breakdowns.session)} />
            <SectionTable title="By Admission Year"      headers={stdH} rows={stdR(breakdowns.year)} />
            <SectionTable title="By Mode of Entry"       headers={stdH} rows={stdR(breakdowns.mode)} />
            <SectionTable title="By Student Level"       headers={stdH} rows={stdR(breakdowns.level)} />
            <SectionTable title="By Department"
                headers={['Code','Department','Total','Admitted','Enrolled','Not Enrolled','Enrolment Rate']}
                rows={breakdowns.dept.map(([k, v]) => [k, deptMap[k]||k, v.total, v.admitted, v.enrolled, v.notEnrolled, pct(v.enrolled,v.admitted)])} />
            <SectionTable title="By Admission Status"
                headers={['Status','Count','% of Total']}
                rows={breakdowns.status.map(([k, v]) => [k, v.total, pct(v.total, totalStudents)])} />
            <SectionTable title="By Programme"           headers={stdH} rows={stdR(breakdowns.programme)} />
            <SectionTable title="By Gender"              headers={stdH} rows={stdR(breakdowns.gender)} />

            {/* ── Charts ── */}
            <div className="pg-break" />
            <h2 style={{ marginBottom: 12 }}>Chart Trends</h2>

            {/* Row 1: Year area + Gender donut */}
            <div className="two-col" style={{ marginBottom: 14 }}>
                <ChartCard title="Year-over-Year Enrolment Trend">
                    <AreaChart breakdown={breakdowns.year} height={220} />
                </ChartCard>
                <ChartCard title="Gender Distribution">
                    <DonutChart breakdown={breakdowns.gender} height={220} />
                </ChartCard>
            </div>

            {/* Row 2: Session column bar */}
            <ChartCard title="Enrolment by Admission Session" style={{ marginBottom: 14 }}>
                <ColumnBarChart breakdown={breakdowns.session} height={240} />
            </ChartCard>

            {/* Row 3: Dept h-bar + Level radial */}
            <div className="two-col" style={{ marginBottom: 14 }}>
                <ChartCard title="Top Departments">
                    <HBarChart breakdown={breakdowns.dept} topN={10} />
                </ChartCard>
                <ChartCard title="Enrolment Rate by Level">
                    <RadialBarChart breakdown={breakdowns.level} />
                </ChartCard>
            </div>

            {/* Row 4: Mode + Status + Enrolled/Not Enrolled */}
            <div className="three-col" style={{ marginBottom: 14 }}>
                <ChartCard title="Mode of Entry">
                    <DonutChart breakdown={breakdowns.mode} height={220} />
                </ChartCard>
                <ChartCard title="Admission Status">
                    <DonutChart breakdown={breakdowns.status} height={220} />
                </ChartCard>
                <ChartCard title="Enrolled vs Not Enrolled">
                    <DonutChart breakdown={[['Enrolled',{total:totalEnrolled}],['Not Enrolled',{total:totalNotEnrolled}]]} height={220} />
                </ChartCard>
            </div>

            {/* Row 5: Programme h-bar */}
            <ChartCard title="Top Programmes" style={{ marginBottom: 0 }}>
                <HBarChart breakdown={breakdowns.programme} topN={10} />
            </ChartCard>
        </div>
    );
});
PrintableReport.displayName = 'PrintableReport';

const mapStateToProps = (state) => ({ loginData: state.loginDetails });
export default connect(mapStateToProps, null)(StudentEnrollmentAnalytics);
